// Run polyfills first
import 'embed/polyfills'

// Run DataDog RUM init next
import 'common/services/RUM'

import * as React from 'react'
import * as ReactDOM from 'react-dom'

import { Provider } from 'react-redux'
import { Action, applyMiddleware, createStore } from 'redux'
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly'
import { createLogger } from 'redux-logger'
import thunkMiddleware from 'redux-thunk'

import { impression, loaded } from 'common/actions'
import { isDev } from 'embed/constants/config'
import GoogleAnalytics from 'common/services/GoogleAnalytics'
import Parent from 'common/services/Parent'
import Tracker from 'common/services/Tracker'
import { testingMiddleware } from 'embed/middleware/Testing'
import tracingMiddleware from 'embed/middleware/Tracing'
import { trackingMiddleware } from 'embed/middleware/Tracking'
import { FacebookPixel } from './components/FacebookPixel'

import { UI_NAME } from 'embed/constants'
import reducers, { initialMultiProductState, MultiProductState } from 'embed/reducers'

import BrowserStateLoader from 'embed/components/BrowserStateLoader/container'
import ConfigProvider from 'embed/components/ConfigProvider'
import { GoogleFontsClient } from 'embed/components/GoogleFontsClient'
import Ui from 'embed/components/Ui'
import AppContainer from './components/App/container'
import GlobalStyles from './components/GlobalStyles'
import ViewportSizeProvider from './components/ViewportSizeProvider'
import { ThemeProvider } from './components/ThemeProvider'

import Session from 'common/services/Session'

import 'iframe-resizer'

import { CreditCardIntroVsPreviewStep, Experiment, Original, Variant } from 'embed/experiments'
import './styles'
import { useTheme } from './hooks'

const composeEnhancers = composeWithDevTools({
  // Add any options here...
})

const multiproductTrackingMiddleware = trackingMiddleware(Tracker, Parent)
const multiproductTestingMiddleware = testingMiddleware()

const middleware = isDev
  ? composeEnhancers(
      applyMiddleware(
        thunkMiddleware,
        createLogger(),
        multiproductTrackingMiddleware,
        tracingMiddleware,
        multiproductTestingMiddleware,
      ),
    )
  : applyMiddleware(thunkMiddleware, multiproductTrackingMiddleware, tracingMiddleware)

// Publish GA events to the parent service
GoogleAnalytics.publishToParent = true

const store = createStore<MultiProductState, Action<unknown>, {}, {}>(reducers, initialMultiProductState, middleware)

// Let session use dispatch as a notification bus
Session.dispatch = store.dispatch

const afterInit = (trackingUuid?: string) => {
  store.dispatch(loaded())
  if (trackingUuid) {
    store.dispatch(impression(trackingUuid))
  }
}

const App = () => {
  const { fontFamilyPrimary, fontFamilySecondary } = useTheme()
  return (
    <>
      <GoogleFontsClient fonts={[fontFamilyPrimary, fontFamilySecondary]} />
      <Ui name={UI_NAME} afterInit={afterInit}>
        <BrowserStateLoader>
          <ConfigProvider waitForParentConfig={true}>
            <FacebookPixel />
            <ViewportSizeProvider />
            <GlobalStyles />
            <Experiment experiment={CreditCardIntroVsPreviewStep}>
              <Original>
                <AppContainer />
              </Original>
              <Variant
                experiment={CreditCardIntroVsPreviewStep}
                variant={CreditCardIntroVsPreviewStep.variants.IntroStep}
              >
                <AppContainer />
              </Variant>
            </Experiment>
          </ConfigProvider>
        </BrowserStateLoader>
      </Ui>
    </>
  )
}

ReactDOM.render(
  <Provider store={store}>
    <ThemeProvider>
      <App />
    </ThemeProvider>
  </Provider>,
  document.getElementById('root'),
)
